'use client';

import type { TemplateProductDetail } from '@/constants';
import { useMemo, useState } from 'react';
import type { PrintAreaTemplate, PrintAreaWithTemplates, PrintPreset } from '../types';
import { calculateProductDisplayPrice } from '../utils';

type SwatchOption = {
  value: string;
  label: string;
  swatchImageUrl: string;
};

type TemplateSubmission = {
  templateId: number;
  /** アクスタ時のみ値あり。ステッカーは null */
  printPresetId: number | null;
};

type UseTemplateSelectionReturn = {
  swatchOptions: SwatchOption[];
  selectedVariantCode: string;
  isSwatchSelected: boolean;
  handleSwatchSelect: (code: string, onJump: (index: number) => void) => void;
  resolveTemplateSubmission: () => TemplateSubmission | null;
  /** 選択中の印刷エリア（先頭固定） */
  selectedPrintArea: PrintAreaWithTemplates | null;
  /** 選択中のテンプレート（アクスタ：レイアウトの線画 1 枚／ステッカー：色×形に対応する 1 枚） */
  selectedTemplate: PrintAreaTemplate | null;
  /** 選択中の preset（アクスタのみ。ステッカーは null） */
  selectedPrintPreset: PrintPreset | null;
  displayPrice: number;
};

const isAcrylicStandDetail = (productDetail: TemplateProductDetail): boolean =>
  productDetail.templateType === 'acrylic_stand';

/**
 * スウォッチ選択肢を組み立てる。商品種別で出所が異なる。
 * - アクスタ: 印刷エリアの `printPresets[]`（色違い = preset）
 * - ステッカー: 印刷エリアの `templates[]`（色違い = template）
 */
export const buildSwatchOptions = (productDetail: TemplateProductDetail): SwatchOption[] => {
  const printArea = productDetail.printAreas[0];
  if (!printArea) return [];

  if (isAcrylicStandDetail(productDetail)) {
    return [...printArea.printPresets]
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((p) => ({
        value: p.variantCode,
        label: p.name,
        swatchImageUrl: p.swatchImagePath,
      }));
  }

  return [...printArea.templates]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((t) => ({
      value: t.backgroundVariantCode,
      label: t.backgroundVariantName,
      swatchImageUrl: t.swatchImagePath,
    }));
};

/**
 * 選択中の variantCode から送信用テンプレ情報を解決する。
 * アクスタは printArea にひも付くテンプレートが 1 枚（線画）の前提で先頭を使う。
 */
export const resolveTemplate = (
  productDetail: TemplateProductDetail,
  selectedVariantCode: string,
): TemplateSubmission | null => {
  if (!selectedVariantCode) return null;
  const printArea = productDetail.printAreas[0];
  if (!printArea) return null;

  if (isAcrylicStandDetail(productDetail)) {
    const preset = printArea.printPresets.find((p) => p.variantCode === selectedVariantCode);
    const template = printArea.templates[0];
    if (!preset || !template) return null;
    return {
      templateId: template.templateId,
      printPresetId: preset.printPresetId,
    };
  }

  const template = printArea.templates.find((t) => t.backgroundVariantCode === selectedVariantCode);
  if (!template) return null;
  return {
    templateId: template.templateId,
    printPresetId: null,
  };
};

export const useTemplateSelection = (productDetail: TemplateProductDetail): UseTemplateSelectionReturn => {
  const swatchOptions = useMemo(() => buildSwatchOptions(productDetail), [productDetail]);

  const [selectedVariantCode, setSelectedVariantCode] = useState(() => swatchOptions[0]?.value ?? '');

  const selectedPrintArea = productDetail.printAreas[0] ?? null;
  const isAcrylic = isAcrylicStandDetail(productDetail);

  const { selectedTemplate, selectedPrintPreset } = useMemo(() => {
    if (!selectedPrintArea || !selectedVariantCode) {
      return { selectedTemplate: null, selectedPrintPreset: null };
    }
    if (isAcrylic) {
      const preset = selectedPrintArea.printPresets.find((p) => p.variantCode === selectedVariantCode) ?? null;
      const template = selectedPrintArea.templates[0] ?? null;
      return { selectedTemplate: template, selectedPrintPreset: preset };
    }
    const template = selectedPrintArea.templates.find((t) => t.backgroundVariantCode === selectedVariantCode) ?? null;
    return { selectedTemplate: template, selectedPrintPreset: null };
  }, [isAcrylic, selectedPrintArea, selectedVariantCode]);

  const handleSwatchSelect = (code: string, onJump: (index: number) => void) => {
    setSelectedVariantCode(code);
    // アクスタはレイアウトあたりテンプレートが 1 枚なのでスライダーをジャンプさせる必要がない
    if (isAcrylic) return;
    const printArea = productDetail.printAreas[0];
    if (!printArea) return;
    const template = printArea.templates.find((t) => t.backgroundVariantCode === code);
    if (!template) return;
    const firstIndex = productDetail.templateImages.findIndex((img) => img.templateId === template.templateId);
    if (firstIndex >= 0) onJump(firstIndex);
  };

  const resolveTemplateSubmission = () => resolveTemplate(productDetail, selectedVariantCode);

  const displayPrice = calculateProductDisplayPrice({ basePrice: productDetail.basePrice });

  return {
    swatchOptions,
    selectedVariantCode,
    isSwatchSelected: selectedVariantCode !== '',
    handleSwatchSelect,
    resolveTemplateSubmission,
    selectedPrintArea,
    selectedTemplate,
    selectedPrintPreset,
    displayPrice,
  };
};
